import { v4 as uuid } from "uuid";
import * as statusConstants from "../constants/status";
import { DEFAULT_ANNOTATION_COLOR } from "../constants/colors";
import * as fusewayStatus from "../constants/fusewayStatus";
import { AssetStyleInfo } from "../model/internal/assetInfo";
import { Asset } from "../model/viewModel/asset";
import { ConnectionPoint } from "../model/viewModel/connectionPoint";
import { GroupedConnectionPoint } from "../model/viewModel/groupedConnectionPoint";
import { GroupedConnection } from "../model/viewModel/groupedConnection";
import { LatLng } from "../model/viewModel/simpleTypes";
import { getDefaultStickyNoteProperties } from "../model/viewModel/stickyNoteProperties";
import { Cable } from "../model/viewModel/cable";
import { Transformer } from "../model/viewModel/transformer";
import { TransformerMounting } from "../model/viewModel/transformerProperties";
import { getCableProperties } from "../states/cable";
import { getConnectionPointProperties } from "../states/connectionPoint";
import { getMotorProperties } from "../states/motor";
import { getPointOfConnectionProperties } from "../states/pointOfConnection";
import { getTransformerProperties } from "../states/transformer";
import { getWelderProperties } from "../states/welder";
import { LocalOverrideConfig } from "../utils/configurationFunctions";
import { consumerDefaults } from "../utils/consumerDefaults";
import { getTransformerPhase } from "../utils/networkFunctions";
import {
  getCutOutSizeDefaultValue,
  getDefaultUnbalancedPercent,
  getDefaultLowLoadedPhase,
} from "../utils/consumerDefaults";

import {
  copyListedProps,
  updateGroupedConnectionForCopyAndPaste,
  updateGroupedConnectionPointsForCopyPasteProperties,
  ALLOW_MOTOR,
  ALLOW_WELDER,
  ALLOW_POINT_OF_CONNECTION,
  ALLOW_CABLE,
  ALLOW_TRANSFORMER,
} from "./copyPasteFormPropertiesUtil";

export const getNewAssetProperties = (
  assetStyleInfo: AssetStyleInfo,
  assetToClone: Asset | undefined,
  reference: any,
  localOverrideConfig: LocalOverrideConfig,
  formState: any,
  transformers: Transformer[],
): { formName: string; newProperties: any } | null => {
  const {
    groundTypeOverrideDefaults: { groundTypeOverride: defaultGroundTypeOverride = "" } = {},
    transformers: { defaults: transformerDefaults },
    cables: { defaults: cableDefaults },
    consumers: consumerReference,
  } = reference;
  const pointOfConnectionEnhancedEnabled =
    formState.clientSettings?.features?.PointOfConnectionEnhancedEnabled ?? false;

  const defaultGeometry: LatLng = new LatLng();

  if (assetStyleInfo.type === "groupedConnection") {
    const defaultProperties = new GroupedConnection(
      uuid(),
      defaultGeometry,
      [],
      "",
      defaultGroundTypeOverride,
    );

    const connectionPointToClone = assetToClone as ConnectionPoint;

    const newGroupedConnection = connectionPointToClone
      ? updateGroupedConnectionForCopyAndPaste(
          connectionPointToClone,
          defaultProperties,
          true,
          false,
        )
      : defaultProperties;

    return { formName: "groupedConnectionProperties", newProperties: newGroupedConnection };
  }

  if (assetStyleInfo.type === "transformer") {
    const mounting: TransformerMounting = assetStyleInfo.class
      .split("-")
      .pop() as TransformerMounting;
    const defaultProperties = getTransformerProperties(
      transformerDefaults[mounting],
      false,
      0,
      mounting,
      defaultGeometry,
      230,
      "Three",
      false,
      "Default",
      false,
      transformerDefaults["fuseSize"],
      false,
      [],
      50,
      false,
      8.0,
      "",
      false,
      null as any,
      null as any,
      null as any,
      reference.constraints.systemVoltage,
      null as any,
      transformerDefaults[mounting + "DefaultPrimaryVoltagekV"],
      false,
    );

    const transformerToClone = assetToClone as Transformer;
    const groupedConnectionPoints = updateGroupedConnectionPointsForCopyPasteProperties(
      transformerToClone?.groupedConnectionPoints,
      defaultGeometry,
    );

    const newProperties = getNewProperties(
      defaultProperties,
      assetStyleInfo.class,
      assetToClone,
      ALLOW_TRANSFORMER,
    );

    return {
      formName: "transformerProperties",
      newProperties: { ...newProperties, groupedConnectionPoints: groupedConnectionPoints },
    };
  }

  if (assetStyleInfo.type === "connectionPoint") {
    if (assetStyleInfo.class === "motor") {
      const defaultProperties = getMotorProperties(
        50,
        false,
        "kW",
        "Three",
        false,
        "dirOnLine",
        false,
        0.3,
        false,
        null,
        5,
        false,
        "perMinute",
        85,
        false,
        null,
        null,
        "",
        "",
        undefined,
      );
      const newProperties = getNewProperties(
        defaultProperties,
        assetStyleInfo.class,
        assetToClone,
        ALLOW_MOTOR,
      );
      return { formName: "motorProperties", newProperties };
    } else if (assetStyleInfo.class === "welder") {
      const defaultProperties = getWelderProperties(
        50,
        false,
        "kVA",
        "Three",
        false,
        30,
        false,
        "perMinute",
        false,
        0.3,
        false,
        85,
        false,
        null,
        null,
        "",
        0,
        "",
      );
      const newProperties = getNewProperties(
        defaultProperties,
        assetStyleInfo.class,
        assetToClone,
        ALLOW_WELDER,
      );
      return { formName: "welderProperties", newProperties };
    } else if (assetStyleInfo.class === "point-of-connection") {
      const includesDownstreamLoad = pointOfConnectionEnhancedEnabled;
      const defaultProperties = getPointOfConnectionProperties(
        0,
        0,
        0,
        includesDownstreamLoad,
        false,
        null,
        "",
        "",
      );
      const newProperties = getNewProperties(
        defaultProperties,
        assetStyleInfo.class,
        assetToClone,
        ALLOW_POINT_OF_CONNECTION,
      );
      return { formName: "pointOfConnectionProperties", newProperties };
    } else {
      const consumer = consumerDefaults(
        consumerReference,
        assetStyleInfo.class,
        localOverrideConfig,
      );
      const groupedConnectionPoint = new GroupedConnectionPoint(
        uuid(),
        getConnectionPointProperties(
          consumer["consumerType"],
          false,
          true,
          1,
          0,
          0,
          0,
          false,
          getDefaultUnbalancedPercent(formState, transformers),
          getDefaultLowLoadedPhase(formState, transformers),
          consumer["consumptionValue1"],
          false,
          consumer["consumptionValue2"],
          false,
          consumer["consumptionType"],
          false,
          defaultGeometry,
          consumer["consumerPhases"],
          false,
          [1, 0, 0, 0],
          assetStyleInfo,
          null,
          false,
          assetStyleInfo.name === "Generator",
          null,
          "",
          statusConstants.NEW,
          getCutOutSizeDefaultValue(assetStyleInfo.class),
        ),
        [],
        1,
        "",
      );
      const defaultProperties = new GroupedConnection(
        uuid(),
        defaultGeometry,
        [groupedConnectionPoint],
        "",
        defaultGroundTypeOverride,
      );

      const newProperties = assetToClone
        ? updateGroupedConnectionForCopyAndPaste(
            assetToClone as ConnectionPoint,
            defaultProperties,
            false,
            false,
          )
        : defaultProperties;

      return { formName: "groupedConnectionProperties", newProperties };
    }
  }

  if (assetStyleInfo.type === "cable") {
    const cable = `mains-${assetStyleInfo.class.split("-")[0]}`;
    const transformerPhase = getTransformerPhase(formState, transformers);
    const defaultProperties = getCableProperties(
      [],
      "",
      "",
      0,
      cableDefaults[cable],
      false,
      null,
      cable,
      false,
      transformerPhase,
      false,
      null,
      0,
      false,
      [],
      false,
      "Default",
      "",
      statusConstants.NEW,
      null,
      fusewayStatus.CLOSED,
    );

    const conductorToClone = assetToClone as Cable;

    const groupedConnectionPoints = updateGroupedConnectionPointsForCopyPasteProperties(
      conductorToClone?.groupedConnectionPoints,
      defaultGeometry,
    );

    const newProperties = getNewProperties(
      defaultProperties,
      assetStyleInfo.class,
      assetToClone,
      ALLOW_CABLE,
    );

    return {
      formName: "cableProperties",
      newProperties: { ...newProperties, groupedConnectionPoints: groupedConnectionPoints },
    };
  }

  if (assetStyleInfo.type === "annotation") {
    if (assetStyleInfo.class === "sticky-note") {
      const defaultProperties = getDefaultStickyNoteProperties();
      return { formName: "stickyNoteProperties", newProperties: defaultProperties };
    } else {
      const shapeColour = formState.shapeAnnotationProperties?.color ?? DEFAULT_ANNOTATION_COLOR;
      const defaultProperties = { color: shapeColour };
      return { formName: "shapeAnnotationProperties", newProperties: defaultProperties };
    }
  }

  return null;
};

function getNewProperties<T extends object>(
  defaultProperties: Partial<T>,
  className: string,
  assetToClone: Asset | undefined,
  propertiesToClone: readonly (keyof T)[],
): Partial<T> {
  if (assetToClone?.styles?.class === className) {
    const clonedProperties = copyListedProps(assetToClone as T, propertiesToClone);
    const newProperties = { ...defaultProperties, ...clonedProperties };
    return newProperties;
  }
  return defaultProperties;
}
